/* eslint-disable */
'use client';

import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Spinner,
  Switch,
  Badge,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Avatar,
  Tag,
  TagLabel,
  VStack,
  Divider,
  Alert,
  AlertIcon,
  AlertDescription,
  IconButton,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  SearchIcon,
  LockIcon,
  UnlockIcon,
  InfoIcon,
} from '@chakra-ui/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import defaultProfilePic from 'assets/img/profile/profile.webp';
import { CSVLink } from 'react-csv';
import axios from 'axios';

const columnHelper = createColumnHelper();

export default function ScreenRecordingPermissions() {
  const [sorting, setSorting] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [toggleLoading, setToggleLoading] = useState({});
  const [confirmModal, setConfirmModal] = useState({ open: false, user: null, newValue: false });
  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'allowed' | 'blocked'

  const itemsPerPage = 10;
  const maxVisiblePages = 5;

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const headerBg = useColorModeValue('gray.100', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.800');

  const baseUrl = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
  const token = localStorage.getItem('token');

  const safeDate = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  };

  // ─── STATS ────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = users.length;
    const allowed = users.filter((u) => u.screenRecordingAllowed === true).length;
    const blocked = total - allowed;
    return { total, allowed, blocked };
  }, [users]);

  // ─── FETCH USERS ──────────────────────────────────────────────────
  // NOTE FOR BACKEND DEV:
  // This component calls GET /api/admin/users — same existing endpoint.
  // Each user object should include a field:  screenRecordingAllowed: Boolean (default: false)
  // New API needed:  PATCH /api/admin/users/:userId/screen-recording
  //   Body: { allowed: true | false }
  //   Returns: { success: true, data: { screenRecordingAllowed: Boolean } }
  const fetchUsers = useCallback(async () => {
    if (!token) {
      toast.error('Session expired. Please login again.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        const data = response.data.data || [];
        setUsers(data);
        setFilteredData(data);
      } else {
        toast.error('Failed to load users');
      }
    } catch (err) {
      console.error('Fetch users error:', err?.response?.data || err.message);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [baseUrl, token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ─── FILTER / SEARCH ──────────────────────────────────────────────
  useEffect(() => {
    let data = [...users];

    if (searchQuery) {
      data = data.filter(
        (u) =>
          u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          u.mobile?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    if (filterMode === 'allowed') {
      data = data.filter((u) => u.screenRecordingAllowed === true);
    } else if (filterMode === 'blocked') {
      data = data.filter((u) => !u.screenRecordingAllowed);
    }

    setFilteredData(data);
    setCurrentPage(1);
  }, [searchQuery, filterMode, users]);

  // ─── TOGGLE PERMISSION ────────────────────────────────────────────
  const handleTogglePermission = async (userId, newValue) => {
    if (toggleLoading[userId]) return;
    setToggleLoading((prev) => ({ ...prev, [userId]: true }));

    try {
      // PATCH /api/admin/users/:userId/screen-recording
      // Body: { allowed: true | false }
      await axios.patch(
        `${baseUrl}/api/admin/users/${userId}/screen-recording`,
        { allowed: newValue },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Update local state immediately
      setUsers((prev) =>
        prev.map((u) =>
          u._id === userId ? { ...u, screenRecordingAllowed: newValue } : u,
        ),
      );

      toast.success(
        newValue
          ? '✅ Screen recording allowed for this user'
          : '🚫 Screen recording blocked for this user',
        { position: 'top-right', autoClose: 2500 },
      );
    } catch (err) {
      console.error('Toggle permission error:', err);
      toast.error('Failed to update permission. Please try again.');
    } finally {
      setToggleLoading((prev) => ({ ...prev, [userId]: false }));
      setConfirmModal({ open: false, user: null, newValue: false });
    }
  };

  const openConfirmModal = (user, newValue) => {
    setConfirmModal({ open: true, user, newValue });
  };

  // ─── BULK ACTIONS ─────────────────────────────────────────────────
  const handleBulkAllow = async () => {
    if (!window.confirm('Allow screen recording for ALL users?')) return;
    // In production, call a bulk endpoint: POST /api/admin/users/screen-recording/bulk
    // { userIds: [...], allowed: true }
    toast.info('Bulk action: This requires a bulk API endpoint.');
  };

  const handleBulkBlock = async () => {
    if (!window.confirm('Block screen recording for ALL users?')) return;
    toast.info('Bulk action: This requires a bulk API endpoint.');
  };

  // ─── PAGINATION ───────────────────────────────────────────────────
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const getVisiblePageNumbers = () => {
    const pages = [];
    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);
    if (end - start + 1 < maxVisiblePages) start = Math.max(1, end - maxVisiblePages + 1);
    if (start > 1) { pages.push(1); if (start > 2) pages.push('...'); }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) { if (end < totalPages - 1) pages.push('...'); pages.push(totalPages); }
    return pages;
  };

  const goToPage = (page) => {
    if (page === '...' || !page) return;
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
  };

  // ─── CSV EXPORT ───────────────────────────────────────────────────
  const csvData = filteredData.map((u) => ({
    ID: u._id,
    Name: u.name,
    Email: u.email,
    Mobile: u.mobile,
    Status: u.status,
    ScreenRecordingAllowed: u.screenRecordingAllowed ? 'Yes' : 'No',
    CreatedAt: safeDate(u.createdAt),
  }));

  // ─── TABLE COLUMNS ────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'sno',
        header: () => <Text textAlign="center" fontSize="xs" fontWeight="700">S.No</Text>,
        cell: ({ row }) => (
          <Text textAlign="center" fontSize="sm" color="gray.500">
            {startIndex + row.index + 1}
          </Text>
        ),
      }),

      columnHelper.accessor('profileImage', {
        header: () => <Text textAlign="center" fontSize="xs" fontWeight="700">Profile</Text>,
        cell: (info) => (
          <Flex justify="center">
            <Avatar
              size="sm"
              src={
                info.getValue()
                  ? info.getValue().startsWith('http')
                    ? info.getValue()
                    : `${baseUrl}${info.getValue()}`
                  : defaultProfilePic
              }
              name={info.row.original.name}
            />
          </Flex>
        ),
      }),

      columnHelper.accessor('name', {
        header: () => <Text textAlign="center" fontSize="xs" fontWeight="700">Name</Text>,
        cell: (info) => (
          <Box textAlign="center">
            <Text fontSize="sm" fontWeight="600">{info.getValue() || '—'}</Text>
            <Text fontSize="xs" color="gray.500">{info.row.original.email || '—'}</Text>
          </Box>
        ),
      }),

      columnHelper.accessor('mobile', {
        header: () => <Text textAlign="center" fontSize="xs" fontWeight="700">Mobile</Text>,
        cell: (info) => (
          <Text textAlign="center" fontSize="sm">{info.getValue() || '—'}</Text>
        ),
      }),

      columnHelper.accessor('status', {
        header: () => <Text textAlign="center" fontSize="xs" fontWeight="700">Account Status</Text>,
        cell: (info) => (
          <Flex justify="center">
            <Badge
              colorScheme={info.getValue() === 'active' ? 'green' : 'red'}
              borderRadius="full"
              px={3}
              py={1}
              fontSize="xs"
              textTransform="capitalize"
            >
              {info.getValue() || 'unknown'}
            </Badge>
          </Flex>
        ),
      }),

      columnHelper.accessor('subscriptionStatus', {
        header: () => <Text textAlign="center" fontSize="xs" fontWeight="700">Subscription</Text>,
        cell: (info) => (
          <Flex justify="center">
            <Badge
              colorScheme={info.getValue() ? 'purple' : 'gray'}
              borderRadius="full"
              px={3}
              py={1}
              fontSize="xs"
            >
              {info.getValue() || 'Free'}
            </Badge>
          </Flex>
        ),
      }),

      // ── KEY COLUMN: Screen Recording Permission ──
      columnHelper.accessor('screenRecordingAllowed', {
        header: () => (
          <Flex justify="center" align="center" gap={1}>
            <Icon as={LockIcon} boxSize={3} />
            <Text fontSize="xs" fontWeight="700">Screen Recording</Text>
          </Flex>
        ),
        cell: ({ row }) => {
          const user = row.original;
          const isAllowed = user.screenRecordingAllowed === true;
          const isLoading = toggleLoading[user._id];

          return (
            <Flex justify="center" align="center" gap={3}>
              <Flex align="center" gap={2}>
                <Switch
                  size="md"
                  colorScheme="teal"
                  isChecked={isAllowed}
                  isDisabled={isLoading}
                  onChange={() => openConfirmModal(user, !isAllowed)}
                />
                {isLoading ? (
                  <Spinner size="xs" color="teal.500" />
                ) : (
                  <Badge
                    colorScheme={isAllowed ? 'teal' : 'red'}
                    variant="subtle"
                    borderRadius="full"
                    px={2}
                    fontSize="xs"
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    {isAllowed ? (
                      <><Icon as={UnlockIcon} boxSize={2.5} /> Allowed</>
                    ) : (
                      <><Icon as={LockIcon} boxSize={2.5} /> Blocked</>
                    )}
                  </Badge>
                )}
              </Flex>
            </Flex>
          );
        },
      }),

      columnHelper.accessor('createdAt', {
        header: () => <Text textAlign="center" fontSize="xs" fontWeight="700">Joined</Text>,
        cell: (info) => (
          <Text textAlign="center" fontSize="xs" color="gray.500">
            {safeDate(info.getValue())}
          </Text>
        ),
      }),
    ],
    [baseUrl, toggleLoading, startIndex],
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  });

  return (
    <>
      {/* ── PAGE HEADER ── */}
      <Box mb={6}>
        <Flex align="center" gap={3} mb={2}>
          <Box
            bg="teal.500"
            borderRadius="12px"
            p={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={LockIcon} color="white" boxSize={5} />
          </Box>
          <Box>
            <Text fontSize="2xl" fontWeight="800" color={textColor}>
              Screen Recording Permissions
            </Text>
            <Text fontSize="sm" color="gray.500">
              Control which users can take screenshots or record their screen in the app
            </Text>
          </Box>
        </Flex>

        {/* Info Alert */}
        {/* <Alert status="info" borderRadius="12px" mt={3} fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            <strong>Flutter Dev Note:</strong> The app checks{' '}
            <code>screenRecordingAllowed</code> field from user API. Only users with this
            flag set to <code>true</code> can record/screenshot. Use{' '}
            <code>PATCH /api/admin/users/:id/screen-recording</code> to update.
          </AlertDescription>
        </Alert> */}
      </Box>

      {/* ── STATS CARDS ── */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <Card
          borderRadius="16px"
          p={5}
          bg={cardBg}
          boxShadow="0px 4px 20px rgba(0,0,0,0.08)"
          borderLeft="4px solid"
          borderLeftColor="blue.400"
        >
          <Stat>
            <StatLabel color="gray.500" fontSize="xs">Total Users</StatLabel>
            <StatNumber fontSize="3xl" fontWeight="800" color="blue.500">
              {loading ? <Spinner size="sm" /> : stats.total}
            </StatNumber>
          </Stat>
        </Card>

        <Card
          borderRadius="16px"
          p={5}
          bg={cardBg}
          boxShadow="0px 4px 20px rgba(0,0,0,0.08)"
          borderLeft="4px solid"
          borderLeftColor="teal.400"
          cursor="pointer"
          onClick={() => setFilterMode('allowed')}
          _hover={{ transform: 'translateY(-2px)', transition: 'all 0.2s' }}
          transition="all 0.2s"
        >
          <Stat>
            <StatLabel color="gray.500" fontSize="xs">
              <Flex align="center" gap={1}>
                <Icon as={UnlockIcon} boxSize={3} />
                Allowed (Can Record)
              </Flex>
            </StatLabel>
            <StatNumber fontSize="3xl" fontWeight="800" color="teal.500">
              {loading ? <Spinner size="sm" /> : stats.allowed}
            </StatNumber>
            <Text fontSize="xs" color="teal.400" mt={1}>Click to filter</Text>
          </Stat>
        </Card>

        <Card
          borderRadius="16px"
          p={5}
          bg={cardBg}
          boxShadow="0px 4px 20px rgba(0,0,0,0.08)"
          borderLeft="4px solid"
          borderLeftColor="red.400"
          cursor="pointer"
          onClick={() => setFilterMode('blocked')}
          _hover={{ transform: 'translateY(-2px)', transition: 'all 0.2s' }}
          transition="all 0.2s"
        >
          <Stat>
            <StatLabel color="gray.500" fontSize="xs">
              <Flex align="center" gap={1}>
                <Icon as={LockIcon} boxSize={3} />
                Blocked (Cannot Record)
              </Flex>
            </StatLabel>
            <StatNumber fontSize="3xl" fontWeight="800" color="red.500">
              {loading ? <Spinner size="sm" /> : stats.blocked}
            </StatNumber>
            <Text fontSize="xs" color="red.400" mt={1}>Click to filter</Text>
          </Stat>
        </Card>
      </SimpleGrid>

      {/* ── MAIN TABLE CARD ── */}
      <Card
        flexDirection="column"
        w="100%"
        px={{ base: '15px', md: '25px' }}
        py="25px"
        borderRadius="16px"
        boxShadow="0px 4px 20px rgba(0, 0, 0, 0.1)"
        bg={cardBg}
        overflowX="auto"
      >
        {/* Header Controls */}
        <Flex
          px={{ base: '10px', md: '0' }}
          mb="20px"
          justifyContent="space-between"
          align="center"
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: '10px', md: '0' }}
          flexWrap="wrap"
        >
          <HStack spacing={3} flexWrap="wrap">
            <Text color={textColor} fontSize={{ base: 'lg', md: 'xl' }} fontWeight="700">
              Users
            </Text>
            {/* Filter Tabs */}
            {['all', 'allowed', 'blocked'].map((mode) => (
              <Button
                key={mode}
                size="sm"
                borderRadius="full"
                variant={filterMode === mode ? 'solid' : 'outline'}
                colorScheme={
                  mode === 'allowed' ? 'teal' : mode === 'blocked' ? 'red' : 'gray'
                }
                onClick={() => setFilterMode(mode)}
                textTransform="capitalize"
              >
                {mode === 'all' ? `All (${stats.total})` :
                 mode === 'allowed' ? `Allowed (${stats.allowed})` :
                 `Blocked (${stats.blocked})`}
              </Button>
            ))}
          </HStack>

          <HStack spacing={3} flexWrap="wrap">
            <InputGroup maxW="260px">
              <InputLeftElement pointerEvents="none">
                <Icon as={SearchIcon} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search name, email, mobile..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                borderRadius="12px"
                size="md"
              />
            </InputGroup>

            <CSVLink data={csvData} filename="screen_recording_permissions.csv" style={{ textDecoration: 'none' }}>
              <Button colorScheme="teal" variant="outline" size="md">
                Export CSV
              </Button>
            </CSVLink>
          </HStack>
        </Flex>

        {loading ? (
          <Flex justify="center" align="center" minH="300px" direction="column" gap={3}>
            <Spinner size="xl" color="teal.500" thickness="4px" />
            <Text color="gray.500" fontSize="sm">Loading users...</Text>
          </Flex>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" color="gray.500" mb="24px" mt="12px" minW="900px">
              <Thead bg={headerBg}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th
                        key={header.id}
                        px={{ base: '8px', md: '16px' }}
                        py="14px"
                        borderColor={borderColor}
                        cursor="pointer"
                        onClick={header.column.getToggleSortingHandler()}
                        textTransform="none"
                        letterSpacing="0"
                      >
                        <Flex justify="center" align="center">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() ? (
                            header.column.getIsSorted() === 'asc' ? (
                              <ArrowUpIcon ml={2} boxSize={3} />
                            ) : (
                              <ArrowDownIcon ml={2} boxSize={3} />
                            )
                          ) : null}
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>

              <Tbody>
                {paginatedData.length === 0 ? (
                  <Tr>
                    <Td colSpan={columns.length} textAlign="center" py="60px">
                      <VStack spacing={2}>
                        <Icon as={InfoIcon} boxSize={8} color="gray.300" />
                        <Text color="gray.500" fontSize="lg" fontWeight="600">
                          No users found
                        </Text>
                        <Text color="gray.400" fontSize="sm">
                          Try changing the filter or search query
                        </Text>
                      </VStack>
                    </Td>
                  </Tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <Tr
                      key={row.id}
                      _hover={{ bg: hoverBg }}
                      transition="background 0.15s"
                      borderLeft={
                        row.original.screenRecordingAllowed
                          ? '3px solid'
                          : '3px solid transparent'
                      }
                      borderLeftColor={
                        row.original.screenRecordingAllowed ? 'teal.400' : 'transparent'
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <Td
                          key={cell.id}
                          px={{ base: '8px', md: '16px' }}
                          py="14px"
                          borderColor={borderColor}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Td>
                      ))}
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        )}

        {/* PAGINATION */}
        <Flex
          justify="space-between"
          align="center"
          px={{ base: '10px', md: '5px' }}
          py="10px"
          flexWrap="wrap"
          gap={3}
        >
          <Text fontSize="sm" color="gray.500">
            Showing {totalItems === 0 ? 0 : startIndex + 1} to{' '}
            {Math.min(endIndex, totalItems)} of {totalItems} users
            {filterMode !== 'all' && (
              <Badge ml={2} colorScheme={filterMode === 'allowed' ? 'teal' : 'red'} borderRadius="full">
                {filterMode}
              </Badge>
            )}
          </Text>

          <HStack spacing={1} flexWrap="wrap">
            <Button
              size="sm"
              isDisabled={currentPage === 1}
              onClick={() => goToPage(currentPage - 1)}
              borderRadius="8px"
            >
              Previous
            </Button>

            {getVisiblePageNumbers().map((page, i) => (
              <Button
                key={i}
                size="sm"
                variant={page === currentPage ? 'solid' : 'outline'}
                colorScheme="teal"
                onClick={() => goToPage(page)}
                isDisabled={page === '...'}
                borderRadius="8px"
                minW="36px"
              >
                {page}
              </Button>
            ))}

            <Button
              size="sm"
              isDisabled={currentPage === totalPages}
              onClick={() => goToPage(currentPage + 1)}
              borderRadius="8px"
            >
              Next
            </Button>
          </HStack>
        </Flex>
      </Card>

      {/* ── CONFIRM TOGGLE MODAL ── */}
      <Modal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, user: null, newValue: false })}
        isCentered
        size="md"
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="16px" overflow="hidden">
          <Box
            bg={confirmModal.newValue ? 'teal.500' : 'red.500'}
            px={6}
            py={4}
          >
            <Flex align="center" gap={3}>
              <Icon
                as={confirmModal.newValue ? UnlockIcon : LockIcon}
                color="white"
                boxSize={6}
              />
              <Text fontSize="lg" fontWeight="700" color="white">
                {confirmModal.newValue
                  ? 'Allow Screen Recording'
                  : 'Block Screen Recording'}
              </Text>
            </Flex>
          </Box>

          <ModalBody pt={6} pb={4}>
            {confirmModal.user && (
              <VStack spacing={4} align="stretch">
                <Flex align="center" gap={3} bg={useColorModeValue('gray.50', 'gray.700')} p={3} borderRadius="10px">
                  <Avatar
                    size="md"
                    src={
                      confirmModal.user.profileImage
                        ? confirmModal.user.profileImage.startsWith('http')
                          ? confirmModal.user.profileImage
                          : `${baseUrl}${confirmModal.user.profileImage}`
                        : defaultProfilePic
                    }
                    name={confirmModal.user.name}
                  />
                  <Box>
                    <Text fontWeight="700">{confirmModal.user.name || '—'}</Text>
                    <Text fontSize="sm" color="gray.500">{confirmModal.user.email || '—'}</Text>
                  </Box>
                </Flex>

                <Text fontSize="sm" color="gray.600">
                  {confirmModal.newValue
                    ? '✅ This user will be able to take screenshots and record their screen inside the app.'
                    : '🚫 This user will NOT be able to take screenshots or record their screen inside the app.'}
                </Text>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter gap={3}>
            <Button
              variant="ghost"
              onClick={() => setConfirmModal({ open: false, user: null, newValue: false })}
            >
              Cancel
            </Button>
            <Button
              colorScheme={confirmModal.newValue ? 'teal' : 'red'}
              isLoading={confirmModal.user ? toggleLoading[confirmModal.user._id] : false}
              onClick={() =>
                handleTogglePermission(confirmModal.user._id, confirmModal.newValue)
              }
            >
              {confirmModal.newValue ? 'Yes, Allow' : 'Yes, Block'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
      />
    </>
  );
}
